/**
 * adjuntos.js — helpers puros para los archivos de un artículo (formulario y vista).
 */

const EXT_TIPO = {
  pdf: "pdf",
  jpg: "imagen",
  jpeg: "imagen",
  png: "imagen",
  gif: "imagen",
  webp: "imagen",
  mp3: "audio",
  wav: "audio",
  m4a: "audio",
  ogg: "audio",
  flac: "audio",
  mp4: "video",
  webm: "video",
};

const EXT_ICONO = {
  pdf: "fa-file-pdf",
  doc: "fa-file-word",
  docx: "fa-file-word",
  xls: "fa-file-excel",
  xlsx: "fa-file-excel",
  csv: "fa-file-csv",
  md: "fa-file-lines",
  markdown: "fa-file-lines",
  txt: "fa-file-lines",
  zip: "fa-file-zipper",
};

export const ORIGEN_LABELS = {
  pdf: "PDF",
  docx: "Word",
  md: "Markdown",
  txt: "texto",
  ocr: "OCR",
  whisper: "transcripción",
  youtube: "YouTube",
  manual: "manual",
};

export function fileExtension(nombre) {
  const parts = String(nombre || "").toLowerCase().split(".");
  return parts.length > 1 ? parts.pop() : "";
}

/** Tipo de artículo que puede representar el archivo como principal, o null. */
export function tipoForFile(nombre) {
  return EXT_TIPO[fileExtension(nombre)] || null;
}

/** Archivos cuyo contenido se puede volcar en el editor de un artículo de texto. */
const EXT_CONTENIDO = new Set(["txt", "md", "markdown", "html", "htm", "docx"]);

export function esArchivoDeContenido(nombre) {
  return EXT_CONTENIDO.has(fileExtension(nombre));
}

export function isImageFile(nombre) {
  return tipoForFile(nombre) === "imagen";
}

/** Archivos que se pueden previsualizar en un modal (PDF y Markdown/texto). */
export function previewKind(nombre) {
  const ext = fileExtension(nombre);
  if (ext === "pdf") return "pdf";
  if (ext === "md" || ext === "markdown") return "md";
  if (ext === "txt") return "txt";
  return null;
}

export function iconForFile(nombre) {
  const ext = fileExtension(nombre);
  const tipo = EXT_TIPO[ext];
  if (tipo === "imagen") return "fa-file-image";
  if (tipo === "audio") return "fa-file-audio";
  if (tipo === "video") return "fa-file-video";
  return EXT_ICONO[ext] || "fa-file";
}

export function formatSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return "";
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Estado visible de una fila del formulario.
 * item: { status: 'uploading' | 'error' | 'ok', errorMsg?, adj? }
 */
export function estadoAdjunto(item) {
  if (item.status === "uploading") return { cls: "text-muted", icon: "fa-circle-notch fa-spin", label: "Procesando..." };
  if (item.status === "error") return { cls: "text-danger", icon: "fa-triangle-exclamation", label: item.errorMsg || "Error al subir" };
  const adj = item.adj || {};
  if (adj.error) return { cls: "text-warning", icon: "fa-triangle-exclamation", label: "No se pudo extraer el texto" };
  if (adj.tieneTexto) return { cls: "text-success", icon: "fa-check", label: `Texto extraído (${ORIGEN_LABELS[adj.origen] || adj.origen || "texto"})` };
  return { cls: "text-muted", icon: "fa-minus", label: "Sin texto" };
}

/** Body para guardar el artículo: lista completa de adjuntos (principal primero) + principal. */
export function buildAdjuntosPayload(principal, adicionales) {
  const ids = adicionales.filter(i => i.status === "ok" && i.adj?.id).map(i => i.adj.id);
  if (principal?.id) return { adjuntoIds: [principal.id, ...ids.filter(id => id !== principal.id)], principalId: principal.id };
  return { adjuntoIds: ids, principalId: null };
}

export function isYoutubeUrl(url) {
  return /(^|\.)youtube\.com\/|youtu\.be\//i.test(String(url || ""));
}
